import React, { useEffect, useState } from "react";
import { Button } from "@mui/material";
import { NitroMarkerBase } from "../../../../components/map/components/NitroMarker";
import TruckIconFO from "../../../../icons/TruckIconFO";
import RailIconFO from "../../../../icons/RailIconFO";
import { Quote } from "../../../../types/IMultiLocationQuoteMobile";
import { FreightTruck, IQuoteExtension } from "../../../../types/IQuotes";
import { calculateFobDifference } from "../../../multiLocationQuotePanel/components/multiLocationQuoteUtils";
import {
  calculateDlvd,
  calculateNb,
} from "../../multiLocQuoteDetailsPanel/multiLocQuoteDetailsUtils";
import { icons } from "../../../../svg";
import { useNitroCAStore } from "../../../../store/CompetitiveAnalysisStore";
import {
  evaluateOriginManagerNbStyleAndDelta,
  formatOriginManagerPriceDisplay,
  scrollElementAboveKeyboard,
} from "../../../../utils/utils";
import "../../multiLocQuoteDetailsPanel/multiLocQuoteDetailsPanel.css";

interface MultiLocQuoteRowProps {
  quote: Quote;
  destinationItem: any;
  kochDlvdMode?: {
    truck: "px" | "alt";
    rail: "px" | "alt";
  };
  nbOverrides: Map<string, number>;
  dlvdOfferInput: string;
  editableRefs: React.MutableRefObject<Map<string, HTMLSpanElement>>;
  setHoveringFreight: (value: { isHovering: boolean; id: string }) => void;
  setDisplayFreightDetailsPanel: (value: boolean) => void;
  setDisplayTruckFreightDetailsPanel: (value: boolean) => void;
  setSelectedTruckDetailRecord: (record: FreightTruck) => void;
  setAnchorForTruckDetailsPanel: (anchor: HTMLElement) => void;
  handleNbEdit: (quoteKey: string, element: HTMLSpanElement | null) => void;
  debounceTimers: React.MutableRefObject<Map<string, NodeJS.Timeout>>;
}

const formatOriginManagerPriceRange = (quote: Quote): string => {
  const formatPrice = (value: number | string | null | undefined): string =>
    formatOriginManagerPriceDisplay(value) ?? "-";
  const mode = (quote.mode || "").toUpperCase();
  const isKoch = quote.competitor !== true;
  let upperBound = `${quote.maxPrice}`;
  let lowerBound = `${quote.minPrice ?? quote.maxPrice}`;

  if (mode === "TRUCK") {
    lowerBound = formatPrice(quote.truckPrice);
  } else if (mode === "RAIL") {
    lowerBound = formatPrice(quote.railPrice);
  } else {
    return `(${upperBound} - ${lowerBound})`;
  }

  if (!isKoch) {
    return `(${lowerBound})`;
  }

  upperBound = formatPrice(quote.postedPrice);
  return `(${upperBound} - ${lowerBound})`;
};

const MultiLocQuoteRow: React.FC<MultiLocQuoteRowProps> = ({
  quote,
  destinationItem,
  kochDlvdMode,
  nbOverrides,
  dlvdOfferInput,
  editableRefs,
  setHoveringFreight,
  setDisplayFreightDetailsPanel,
  setDisplayTruckFreightDetailsPanel,
  setSelectedTruckDetailRecord,
  setAnchorForTruckDetailsPanel,
  handleNbEdit,
  debounceTimers,
}) => {
  const railMinCarsKey =
    quote.mode?.toUpperCase() === "RAIL" &&
    quote.freight &&
    "minCars" in quote.freight &&
    typeof quote.freight.minCars === "number"
      ? `${quote.freight.minCars}`
      : "default";

  const quoteKey = `${quote.origin.id || quote.origin.name}-${quote.mode}-${
    quote.competitor ? "competitor" : "koch"
  }-${railMinCarsKey}`;
  const initialNetback = quote.maxPrice ?? 0;
  const minPrice = quote.minPrice ?? quote.maxPrice ?? initialNetback;
  const nbOverride = nbOverrides.get(quoteKey);

  let finalNbValue = initialNetback;
  if (dlvdOfferInput && quote.freight?.totalFreight != null) {
    const inputValue = parseFloat(dlvdOfferInput);
    if (!Number.isNaN(inputValue)) {
      finalNbValue = inputValue - quote.freight.totalFreight;
    }
  } else if (nbOverride != null) {
    finalNbValue = nbOverride;
  }

  const isNbModified = nbOverride != null || !!dlvdOfferInput;

  const fobDifference = calculateFobDifference(
    Math.round(finalNbValue),
    Math.round(initialNetback),
    Math.round(minPrice),
    isNbModified,
  );

  const isFreightValid =
    quote.freight && "validation" in quote.freight
      ? (quote.freight.validation as { isValid: boolean }).isValid
      : true;

  const isQuoteRate =
    quote.mode?.toUpperCase() === "RAIL" &&
    !quote.competitor &&
    (quote.freight as any)?.rateType === "Quote";
  const isRailTeamNotifiedAPI = quote.freight?.railTeamNotified;

  const [railTeamNotifiedRows, setRailFreightDetailContext] = useNitroCAStore(
    (state) => [state.railTeamNotifiedRows, state.setRailFreightDetailContext],
  );
  const isRailTeamNotified =
    railTeamNotifiedRows.has(quoteKey) || isRailTeamNotifiedAPI;
  type RailFreightColorState = "default" | "invalid" | "quoted" | "notified";
  let railFreightColorState: RailFreightColorState = "default";
  if (isRailTeamNotified) {
    railFreightColorState = "notified";
  } else if (isQuoteRate) {
    railFreightColorState = "quoted";
  } else if (!isFreightValid) {
    railFreightColorState = "invalid";
  }

  const railFreightColors: Record<
    RailFreightColorState,
    { text: string; hover: string }
  > = {
    default: { text: "#475569", hover: "#2563EB" },
    invalid: { text: "#DC2626", hover: "#2563EB" },
    quoted: { text: "#1D4ED8", hover: "#1D4ED8" },
    notified: { text: "#009F5C", hover: "#009F5C" },
  };

  const { text: railFreightTextColor, hover: railFreightHoverColor } =
    railFreightColors[railFreightColorState];

  const maxPrice = quote.maxPrice ?? 0;
  const minPriceValue = quote.minPrice ?? quote.maxPrice ?? 0;

  const nbDisplayValue = calculateNb(
    quote,
    quoteKey,
    nbOverrides,
    dlvdOfferInput,
    {
      truckDlvdOption: kochDlvdMode?.truck,
      railDlvdOption: kochDlvdMode?.rail,
    },
  );
  const valueLength = nbDisplayValue.replace(/\$|,|-/g, "").length;
  let nbFontSize = "20px";
  if (valueLength >= 5) {
    nbFontSize = "12px";
  } else if (valueLength === 4) {
    nbFontSize = "14px";
  } else if (valueLength === 3) {
    nbFontSize = "15px";
  }

  const [liveNb, setLiveNb] = useState<number | null>(null);

  const parseNbFromString = (s: string) => {
    const raw = (s || "").toString().replace(/[^0-9.-]/g, "");
    const n = parseFloat(raw || "");
    return raw.length > 0 && !Number.isNaN(n) ? n : null;
  };

  useEffect(() => {
    setLiveNb(parseNbFromString(nbDisplayValue));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nbDisplayValue]);

  const nbForStyling = liveNb != null ? liveNb : finalNbValue;
  // Only show origin-manager highlight/delta once the user has actually
  // changed the NB (override or DLVD input) — never on initial load.
  const originManagerNbStyle =
    isNbModified && Number.isFinite(nbForStyling)
      ? evaluateOriginManagerNbStyleAndDelta(
          quote,
          Math.round(nbForStyling),
          !!dlvdOfferInput,
        )
      : null;

  let deltaDisplay = null;

  try {
    if (originManagerNbStyle?.deltaText) {
      deltaDisplay = (
        <span
          style={{
            color:
              originManagerNbStyle.deltaColor ||
              originManagerNbStyle.netbackTextColor,
            fontWeight: 600,
          }}
        >
          {originManagerNbStyle.deltaText}
        </span>
      );
    } else {
      const nbForDelta = liveNb != null ? liveNb : finalNbValue;
      if (nbForDelta > maxPrice) {
        const diff = (nbForDelta - maxPrice).toFixed(0);
        if (diff !== "0") {
          deltaDisplay = (
            <span
              style={{ color: "rgb(22, 101, 52)", fontWeight: 600 }}
            >{` +${diff}`}</span>
          );
        }
      } else if (nbForDelta < minPriceValue) {
        const diff = (minPriceValue - nbForDelta).toFixed(0);
        if (diff !== "0") {
          deltaDisplay = (
            <span
              style={{ color: "rgb(153, 27, 27)", fontWeight: 600 }}
            >{` -${diff}`}</span>
          );
        }
      }
    }
  } catch (e) {
    console.error("Error calculating delta display:", e);
  }

  useEffect(() => {
    if (dlvdOfferInput && quote.freight?.totalFreight != null) {
      if (nbOverrides.has(quoteKey)) {
        nbOverrides.delete(quoteKey);
      }

      const element = editableRefs.current.get(quoteKey);
      if (element) {
        element.textContent = nbDisplayValue;
      }
    }
  }, [
    dlvdOfferInput,
    quoteKey,
    nbDisplayValue,
    editableRefs,
    quote.freight?.totalFreight,
    nbOverrides,
  ]);

  useEffect(() => {
    const dlvdElement = document.getElementById(`dlvd-${quoteKey}`);
    if (dlvdElement) {
      const dlvdValue = calculateDlvd(
        quote,
        quoteKey,
        nbOverrides,
        dlvdOfferInput,
        {
          truckDlvdOption: kochDlvdMode?.truck,
          railDlvdOption: kochDlvdMode?.rail,
        },
      );
      dlvdElement.textContent = dlvdValue;
    }
  }, [
    nbOverrides,
    dlvdOfferInput,
    quoteKey,
    quote,
    kochDlvdMode?.truck,
    kochDlvdMode?.rail,
  ]);

  const handleInput = (
    e: React.FormEvent<HTMLDivElement | HTMLInputElement>,
  ) => {
    const target = e.currentTarget;
    const rawInput = target.textContent?.replace(/[^0-9.-]/g, "") || "";
    const newValue = parseFloat(rawInput || "0");
    const roundedValue = Math.round(newValue);

    const updateStyles = (style: any, defaultColor = "#000") => {
      target.style.color = style?.netbackTextColor || defaultColor;
      target.style.backgroundColor = style?.backgroundColor || "transparent";
      target.style.padding = style?.hasBackgroundChange ? "2px 4px" : "0";
      target.style.borderRadius = style?.hasBackgroundChange ? "3px" : "0";
    };

    if (rawInput.length > 0 && !Number.isNaN(newValue)) {
      let styleData;

      styleData = evaluateOriginManagerNbStyleAndDelta(
        quote,
        roundedValue,
        !!dlvdOfferInput,
      );

      if (!styleData) {
        styleData = calculateFobDifference(
          roundedValue,
          Math.round(initialNetback),
          Math.round(minPrice),
          true,
        );
      }

      updateStyles(styleData);
      setLiveNb(newValue);
    } else {
      const fallbackStyle = originManagerNbStyle || fobDifference;
      updateStyles(fallbackStyle);
      setLiveNb(null);
    }

    if (!Number.isNaN(newValue) && quote.freight?.totalFreight != null) {
      const dlvdElement = document.getElementById(`dlvd-${quoteKey}`);
      if (dlvdElement) {
        dlvdElement.textContent = `$${(
          newValue + quote.freight.totalFreight
        ).toFixed(0)}`;
      }
    }
  };
  const selectNbValue = (event: React.SyntheticEvent<HTMLSpanElement>) => {
    const selection = event.currentTarget.ownerDocument.getSelection();
    if (!selection) return;

    const range = event.currentTarget.ownerDocument.createRange();
    range.selectNodeContents(event.currentTarget);
    selection.removeAllRanges();
    selection.addRange(range);
  };
  return (
    <div key={quoteKey} className="origin-row-horizontal">
      {/* Column 1: Info (51% width) */}
      <div className="quote-row-column-1">
        <div style={{ flexShrink: 0 }}>
          <NitroMarkerBase
            width={36}
            height={48}
            type={quote.competitor ? "competitor" : "koch"}
          />
        </div>
        <div className="quote-row-info-column" style={{ flex: 1, minWidth: 0 }}>
          <div className="primary-name">{quote.origin.name}</div>
          <div className="price-range">
            {formatOriginManagerPriceRange(quote)}
            <span id={`delta-display-${quoteKey}`}>{deltaDisplay}</span>
          </div>
        </div>
      </div>

      {/* Column 2: DLVD & Freight (28% width) */}
      <div className="quote-row-column-2">
        <div className="quote-row-dlvd-container">
          <span id={`dlvd-${quoteKey}`} className="quote-row-dlvd-value">
            {calculateDlvd(quote, quoteKey, nbOverrides, dlvdOfferInput, {
              truckDlvdOption: kochDlvdMode?.truck,
              railDlvdOption: kochDlvdMode?.rail,
            })}
          </span>
          <span className="unit-label">DLVD</span>
        </div>
        <div className="quote-row-freight-container">
          <div
            style={{ display: "flex", flexDirection: "column", width: "100%" }}
          >
            {quote.mode?.toUpperCase() === "TRUCK" ? (
              <div
                id={`icon-${quoteKey}`}
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <TruckIconFO color="#60A5FA" />
                <Button
                  id={`freight-value-${quoteKey}-${
                    quote.competitor ? "competitor" : "koch"
                  }`}
                  className="freight-value"
                  sx={{
                    borderRadius: "3px",
                    color: isFreightValid ? "#475569" : "#DC2626",
                    backgroundColor: isFreightValid ? "transparent" : "#FFEFEF",
                    minWidth: "auto",
                    padding: "2px 0px",
                    paddingRight: "1px",
                    textTransform: "none",
                    fontSize: "14px",
                    fontWeight: 400,
                    lineHeight: "130%",
                    "&:hover": {
                      color: isFreightValid ? "#2563EB" : "#991B1B",
                      backgroundColor: isFreightValid ? "#E8EFF7" : "#EEDEDE",
                    },
                  }}
                  onMouseEnter={() =>
                    setHoveringFreight({
                      isHovering: true,
                      id: quote.origin.id || quote.origin.name,
                    })
                  }
                  onMouseLeave={() =>
                    setHoveringFreight({
                      isHovering: false,
                      id: "",
                    })
                  }
                  onClick={(event) => {
                    if (
                      quote.mode?.toUpperCase() === "TRUCK" &&
                      isFreightValid
                    ) {
                      setDisplayTruckFreightDetailsPanel(true);
                      setSelectedTruckDetailRecord(
                        quote.freight as FreightTruck,
                      );
                      setAnchorForTruckDetailsPanel(event.currentTarget);
                    }
                  }}
                >
                  <span id={`freight-${quoteKey}`}>
                    {quote.freight?.totalFreight != null
                      ? `$${quote.freight.totalFreight?.toFixed(2)}`
                      : "$-"}
                  </span>
                </Button>
              </div>
            ) : (
              <>
                <div
                  id={`icon-${quoteKey}`}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <RailIconFO color="#F97316" />
                  <span
                    id={`rail-min-cars-origin-row-${quoteKey}`}
                    style={{
                      fontFamily: "Inter",
                      fontWeight: 300,
                      fontSize: "14px",
                      lineHeight: "130%",
                      letterSpacing: "0%",
                      verticalAlign: "middle",
                      color: "#8997AB",
                      paddingLeft: "4px",
                    }}
                  >
                    {quote.mode?.toUpperCase() === "RAIL" &&
                      (quote.freight &&
                      "minCars" in quote.freight &&
                      typeof quote.freight.minCars === "number" &&
                      quote.freight.minCars > 1 ? (
                        <>{quote.freight.minCars}+</>
                      ) : (
                        "1+"
                      ))}
                  </span>
                </div>
                <div style={{ width: "100%", marginTop: 2 }}>
                  <Button
                    id={`rail-freight-value-${quoteKey}-${
                      quote.competitor ? "competitor" : "koch"
                    }`}
                    className="freight-value"
                    sx={{
                      borderRadius: "3px",
                      color: railFreightTextColor,
                      backgroundColor: isFreightValid
                        ? "transparent"
                        : "#FFEFEF",
                      minWidth: "auto",
                      padding: "2px 0px",
                      paddingRight: "1px",
                      textTransform: "none",
                      fontSize: "14px",
                      fontWeight: 400,
                      lineHeight: "130%",
                      gap: "4px",
                      "&:hover": {
                        color: railFreightHoverColor,
                        backgroundColor: isFreightValid ? "#E8EFF7" : "#EEDEDE",
                      },
                    }}
                    onMouseEnter={() =>
                      setHoveringFreight({
                        isHovering: true,
                        id: quote.origin.id || quote.origin.name,
                      })
                    }
                    onMouseLeave={() =>
                      setHoveringFreight({
                        isHovering: false,
                        id: "",
                      })
                    }
                    onClick={() => {
                      if (quote.mode?.toUpperCase() === "RAIL") {
                        setRailFreightDetailContext({
                          quote: quote as unknown as IQuoteExtension,
                          destination: destinationItem,
                          rowKey: quoteKey,
                        });
                        setDisplayFreightDetailsPanel(true);
                      }
                    }}
                  >
                    <span id={`freight-${quoteKey}`}>
                      {quote.freight?.totalFreight != null
                        ? `$${quote.freight.totalFreight?.toFixed(2)}`
                        : "$-"}
                    </span>
                    {isQuoteRate && (
                      <img
                        src={
                          isRailTeamNotified
                            ? icons.CheckmarkPageIcon
                            : icons.NoteIcon
                        }
                        alt={
                          isRailTeamNotified
                            ? "Rail Team Notified"
                            : "Quote rate"
                        }
                        style={{ width: 14, height: 14, flexShrink: 0 }}
                      />
                    )}
                    {/* {isFreightValid ? (
                  <InformationIcon fill={freightFill} />
                ) : (
                  <WarningTriangleIcon stroke={freightStroke} />
                )} */}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Column 3: NB (20% width) */}
      <div className="quote-row-column-3">
        <div className="quote-row-nb-container">
          <span
            id={`nb-${quoteKey}-${quote.competitor ? "competitor" : "koch"}`}
            className={`quote-row-nb-value${
              (
                originManagerNbStyle
                  ? originManagerNbStyle.hasBackgroundChange
                  : fobDifference.hasBackgroundChange
              )
                ? " nb-highlighted"
                : ""
            }`}
            role="textbox"
            tabIndex={0}
            aria-label="Net back price"
            inputMode="numeric"
            ref={(el) => {
              if (el) {
                editableRefs.current.set(quoteKey, el);
              }
            }}
            contentEditable
            suppressContentEditableWarning
            onFocus={(e) => {
              selectNbValue(e);
              scrollElementAboveKeyboard(
                e.currentTarget.closest<HTMLElement>(".origin-row-horizontal"),
                ".quote-panel-content",
              );
            }}
            onClick={selectNbValue}
            onContextMenu={(event) => event.preventDefault()}
            onInput={handleInput}
            onBlur={(e) => {
              const timer = debounceTimers.current.get(quoteKey);
              if (timer) {
                clearTimeout(timer);
              }
              handleNbEdit(quoteKey, e.currentTarget);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.currentTarget.blur();
              }
            }}
            style={
              {
                "--nb-color":
                  originManagerNbStyle?.netbackTextColor ||
                  fobDifference.netbackTextColor ||
                  "#000",
                "--nb-bg":
                  originManagerNbStyle?.backgroundColor ||
                  fobDifference.backgroundColor ||
                  "transparent",
                "--nb-font-size": originManagerNbStyle?.forceFontSize20
                  ? "20px"
                  : nbFontSize,
              } as React.CSSProperties
            }
          >
            {nbDisplayValue}
          </span>
          <span className="unit-label">NB</span>
        </div>
      </div>
    </div>
  );
};

export default MultiLocQuoteRow;
